import { WorkerRole } from '../../globalTypes';
import i18n from '../../i18n';
import yup, { InferObjectToYupShape, RTEContentRequired, validNumberString } from '../../yup';
import {
  ImageUploadWithLabelFormValues,
  imageUploadWithLabelValidationSchema,
} from './components/OvertimeUploadImageWidget/OvertimeUploadImageWidgetEntry/components/ImageUploadWithLabel';

export type BaseOvertimeEmployee = {
  hours?: string;
  materialSurcharge?: string;
  extraHours?: string;
  machinesUsage?: string;
};

export enum OvertimeEmployeeType {
  CUSTOM = 'CUSTOM',
  SAP = 'SAP',
  DELETED_SAP = 'DELETED_SAP',
}

export type OvertimeCustomEmployee = BaseOvertimeEmployee & {
  type: OvertimeEmployeeType.CUSTOM;
  fullName: string;
  role: WorkerRole;
};

export type OvertimeSAPEmployee = BaseOvertimeEmployee & {
  type: OvertimeEmployeeType.SAP;
  employeeId: string;
  fullName: string;
  role?: WorkerRole;
  sapRole: string;
  automaticRole: boolean;
};

export type OvertimeDeletedSAPEmployee = BaseOvertimeEmployee & {
  type: OvertimeEmployeeType.DELETED_SAP;
  employeeId: string;
  fullName?: string;
  role?: never;
  sapRole?: never;
  automaticRole: true;
};

export type OvertimeFormEmployee =
  | OvertimeCustomEmployee
  | OvertimeSAPEmployee
  | OvertimeDeletedSAPEmployee;

export type OvertimeProjectInformationValues = {
  no?: string;
  date: string;
  projectNumber?: string;
  clientName?: string;
  professions?: string[];
  clientReference?: string;
  description?: string;
  orderDate: string;
  endDate?: string;
};

export type OvertimeWorkInformationValues = {
  workName: string;
  workDescription: string;
};

export type OvertimeFormValues = OvertimeProjectInformationValues &
  OvertimeWorkInformationValues & {
    images: ImageUploadWithLabelFormValues[];
    employees: OvertimeFormEmployee[];
  };

const MAX_ALLOWED_NUMBER_VALUE = 99999999.9;
export const MATERIAL_SURCHARGE_CHARACTER_LIMIT = 250;
export const MACHINES_USAGE_CHARACTER_LIMIT = 250;

export const LEGACY_WORK_NAME_CHARACTER_LIMIT = 255;
export const WORK_NAME_CHARACTER_LIMIT = 50;

const createBaseEmployeeSchema = (): yup.ObjectSchema<
  InferObjectToYupShape<BaseOvertimeEmployee>
> =>
  yup.object().shape({
    hours: validNumberString().max(MAX_ALLOWED_NUMBER_VALUE),
    materialSurcharge: yup.string().trim().min(1).max(MATERIAL_SURCHARGE_CHARACTER_LIMIT),
    extraHours: validNumberString().max(MAX_ALLOWED_NUMBER_VALUE),
    machinesUsage: yup.string().trim().min(1).max(MACHINES_USAGE_CHARACTER_LIMIT),
  });

const createCustomEmployeeSchema = (): yup.ObjectSchema<
  InferObjectToYupShape<OvertimeCustomEmployee>
> =>
  createBaseEmployeeSchema().shape({
    type: yup
      .mixed<OvertimeEmployeeType.CUSTOM>()
      .oneOf([OvertimeEmployeeType.CUSTOM])
      .default(OvertimeEmployeeType.CUSTOM)
      .required(),
    fullName: yup.string().required(),
    role: yup.mixed<WorkerRole>().oneOf(Object.values(WorkerRole)).required(),
  });

const createSapEmployeeSchema = (): yup.ObjectSchema<
  InferObjectToYupShape<Omit<OvertimeSAPEmployee, 'sapRole'>>
> =>
  createBaseEmployeeSchema().shape({
    type: yup
      .mixed<OvertimeEmployeeType.SAP>()
      .oneOf([OvertimeEmployeeType.SAP])
      .default(OvertimeEmployeeType.SAP)
      .required(),
    fullName: yup.string().required(),
    employeeId: yup.string().required(),
    role: yup
      .mixed<WorkerRole>()
      .oneOf(Object.values(WorkerRole))
      .when('automaticRole', {
        is: false,
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.notRequired(),
      }),
    automaticRole: yup.boolean().required(),
  });

const createDeletedSapEmployeeSchema = (): yup.ObjectSchema<
  InferObjectToYupShape<Omit<OvertimeDeletedSAPEmployee, 'role' | 'sapRole'>>
> =>
  createBaseEmployeeSchema()
    .shape({
      type: yup
        .mixed<OvertimeEmployeeType.DELETED_SAP>()
        .oneOf([OvertimeEmployeeType.DELETED_SAP])
        .default(OvertimeEmployeeType.DELETED_SAP)
        .required(),
      fullName: yup.string().default('').optional(),
      employeeId: yup.string().default('').required(),
      automaticRole: yup.mixed<true>().oneOf([true]).default(true).required(),
    })
    .test({
      name: 'is-deleted-employee',
      test(employee: OvertimeDeletedSAPEmployee, ctx: yup.TestContext) {
        return ctx.createError({
          path: `${ctx.path}.notFound`,
          message: i18n.t('employeeForm.validation.notFound', { employeeId: employee.employeeId }),
        });
      },
    });

function withRequiredOvertimeDetails(
  schema: yup.ObjectSchema<InferObjectToYupShape<BaseOvertimeEmployee>>,
) {
  return schema.test({
    name: 'overtime-details-required',
    test(employee: BaseOvertimeEmployee, ctx) {
      const { hours, extraHours, machinesUsage, materialSurcharge } = employee;

      if (hours || extraHours || machinesUsage || materialSurcharge) {
        return true;
      }

      return ctx.createError({
        path: `${ctx.path}.overtimeDetails`,
        message: i18n.t('employeeForm.validation.overtimeDetailsRequired'),
      });
    },
  });
}

const createEmployeeValidationSchema = () =>
  yup.lazy((employee: OvertimeFormEmployee) => {
    switch (employee.type) {
      case OvertimeEmployeeType.SAP:
        return withRequiredOvertimeDetails(createSapEmployeeSchema());
      case OvertimeEmployeeType.DELETED_SAP:
        return withRequiredOvertimeDetails(createDeletedSapEmployeeSchema());
      case OvertimeEmployeeType.CUSTOM:
      default:
        return withRequiredOvertimeDetails(createCustomEmployeeSchema());
    }
  });

export const createOvertimeValidationSchema = (): yup.ObjectSchema<
  InferObjectToYupShape<OvertimeFormValues>
> =>
  yup.object().shape<InferObjectToYupShape<OvertimeFormValues>>({
    no: yup
      .number()
      .transform((value, originalValue) => {
        const integerPattern = /^[1-9]\d*$/;
        if (!integerPattern.test(originalValue)) {
          return NaN;
        }

        return value;
      })
      .integer(i18n.t('validation.reportNumber.invalid'))
      .min(1)
      .max(Number.MAX_SAFE_INTEGER)
      .typeError(i18n.t('validation.reportNumber.invalid')),
    date: yup.lazy(() =>
      yup
        .date()
        .typeError(i18n.t('validation.date.typeError'))
        .required()
        .max(new Date(), i18n.t('validation.date.future')),
    ),
    projectNumber: yup.string(),
    clientName: yup.string().required(),
    professions: yup.array(yup.string().min(1)).min(1).required(),
    clientReference: yup.string().optional(),
    description: yup.string().optional(),
    orderDate: yup.lazy(() =>
      yup
        .date()
        .typeError(i18n.t('validation.date.typeError'))
        .required()
        .max(new Date(), i18n.t('validation.date.future')),
    ),
    endDate: yup
      .date()
      .typeError(i18n.t('validation.date.typeError'))
      .min(yup.ref('orderDate'), i18n.t('validation.date.past')),
    workName: yup.string().max(WORK_NAME_CHARACTER_LIMIT).required(),
    workDescription: RTEContentRequired(),
    images: yup.array().of(imageUploadWithLabelValidationSchema.defined()).defined(),
    employees: yup
      .array<OvertimeFormEmployee>()
      .of(createEmployeeValidationSchema() as never)
      .min(1),
  });

export const createLegacyOvertimeValidationSchema = (): yup.ObjectSchema<
  InferObjectToYupShape<OvertimeFormValues>
> =>
  createOvertimeValidationSchema().shape({
    workName: yup.string().max(LEGACY_WORK_NAME_CHARACTER_LIMIT).required(),
    professions: yup.array(yup.string().min(1)).optional(),
    employees: yup
      .array<OvertimeFormEmployee>()
      .of(
        createCustomEmployeeSchema()
          .clone()
          .shape({ machinesUsage: validNumberString().max(MAX_ALLOWED_NUMBER_VALUE) }),
      )
      .min(1),
  });

export function getSAPEmployeeRoleDefaults(
  automaticRole: boolean,
): Pick<OvertimeSAPEmployee, 'automaticRole' | 'role'> {
  return { automaticRole, role: automaticRole ? undefined : WorkerRole.Fa };
}

export function getOvertimeFormInitialValues({
  initialValues,
  pdfRedesignEnabled,
  employeeAutomaticRoleSelection,
}: {
  initialValues?: Partial<OvertimeFormValues>;
  pdfRedesignEnabled: boolean;
  employeeAutomaticRoleSelection: boolean;
}): OvertimeFormValues {
  const baseEmployee: BaseOvertimeEmployee = {
    hours: '',
    extraHours: '',
    machinesUsage: '',
    materialSurcharge: '',
  };

  const employee: OvertimeFormEmployee = pdfRedesignEnabled
    ? {
        type: OvertimeEmployeeType.SAP,
        employeeId: '',
        fullName: '',
        sapRole: '',
        ...getSAPEmployeeRoleDefaults(employeeAutomaticRoleSelection),
        ...baseEmployee,
      }
    : { type: OvertimeEmployeeType.CUSTOM, fullName: '', role: WorkerRole.Fa, ...baseEmployee };

  return {
    no: '',
    date: '',
    projectNumber: initialValues?.projectNumber ?? '',
    clientName: initialValues?.clientName ?? '',
    professions: [],
    orderDate: '',
    endDate: '',
    workName: '',
    workDescription: '',
    images: [],
    employees: [employee],
    ...initialValues,
  };
}

function castOvertimeFormEmployeeValues(
  employees: OvertimeFormValues['employees'],
): OvertimeFormValues['employees'] {
  return employees.map((employee) => ({
    ...employee,
    hours: employee.hours ? employee.hours.replace(',', '.') : undefined,
    materialSurcharge: employee.materialSurcharge,
    extraHours: employee.extraHours ? employee.extraHours.replace(',', '.') : undefined,
    machinesUsage: employee.machinesUsage,
  }));
}

export function castOvertimeFormValues(values: OvertimeFormValues): OvertimeFormValues {
  return {
    ...values,
    clientReference: values.clientReference || undefined,
    professions: values.professions || [],
    description: values.description || undefined,
    employees: castOvertimeFormEmployeeValues(values.employees),
  };
}
